import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {TicketsService} from '../../../services/tickets/tickets.service';
import {ScrollLoad} from '../../../components/scroll-load-component/ScrollLoad';
import {
  Customer,
  EventLimited,
  EventStatus,
  EventType,
  SalespointWithAlias,
  SearchTicket,
  SortOrderEnum
} from '../../../generated/sales-terminal-client';
import {EventService} from '../../../services/event/event.service';
import {UtilService} from '../../../services/util/util.service';
import {DatePickerConfig} from '../../../components/datepicker/datepicker.component';
import {DATEPICKER_PRESETS_MAPPED} from '../../../components/datepicker/datepicker.constants';
import {cloneDeep, find} from 'lodash-es';
import {CustomerService} from '../../../services/customer/customer.service';
import {forkJoin, Observable, ReplaySubject, Subscription} from 'rxjs';
import {SalespointsService} from '../../../services/salespoints/salespoints.service';
import {finalize} from 'rxjs/operators';
import {ValidationService} from '../../../services/validation/validation.service';
import {SortByOption} from '../../../interfaces/sort-by-option.interface';
import {PageableResult, SortableSearch} from '../../../components/scroll-load-component/scroll-load.component';
import moment from 'moment/moment';
import {DataTable, DataTableRowItemType} from '../../../components/data-table/data-table.interfaces';
import {RouteService} from '../../../services/route/route.service';
import {AuthService} from '../../../services/auth/auth.service';
import {SELF_SERVICE_EXCLUDED_STATUSES} from '../event-search/event-search.constants';
import {Params} from '@angular/router';
import {ViewService} from '../../../services/view/view.service';
import {STORAGE_REPORTS_LEGAL_PERSON_ID} from '../../reports/reports.constants';
import {EventSearch} from '../../../services/reports/reports.service';

export interface TicketsSearchFilters extends SortableSearch {
  dateStart?: string;
  dateEnd?: string;
  ticketId?: number;
  barcode?: string;
  customerUuid?: string;
  customerEmail?: string;
  eventId?: number;
  invoiceNumber?: string;
  salespointId?: number;
}

@Component({
  selector: 'app-tickets',
  templateUrl: './tickets.component.html',
  styleUrl: './tickets.component.scss'
})
export class TicketsComponent implements OnInit, AfterViewInit, OnDestroy {
  public static MAX_DATE_RANGE = 31;

  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('opTimeTemplate') opTimeTemplate: TemplateRef<any>;
  @ViewChild('eventTemplate') eventTemplate: TemplateRef<any>;
  @ViewChild('ticketTypeTemplate') ticketTypeTemplate: TemplateRef<any>;
  @ViewChild('priceTemplate') priceTemplate: TemplateRef<any>;
  @ViewChild('customerTemplate') customerTemplate: TemplateRef<any>;
  @ViewChild('deliveryEmailTemplate') deliveryEmailTemplate: TemplateRef<any>;
  @ViewChild('opSalespointTemplate') opSalespointTemplate: TemplateRef<any>;
  @ViewChild('opSellerTemplate') opSellerTemplate: TemplateRef<any>;

  public isLoading: boolean;
  public showMobileFilters: boolean;
  public eventScrollLoad: ScrollLoad<EventLimited> = this.eventService.getScrollLoad(50);
  public customerScrollLoad: ScrollLoad<Customer> = this.customerService.getScrollLoad(10);
  public filters: TicketsSearchFilters = {
    dateStart: null,
    dateEnd: null,
    ticketId: null,
    barcode: null,
    customerUuid: null,
    customerEmail: null,
    eventId: null,
    invoiceNumber: null,
    salespointId: null
  };
  private _defaultDatePickerConfig: DatePickerConfig = {
    range: true,
    allowDateClear: true,
    noHint: true,
    dateRanges: [DATEPICKER_PRESETS_MAPPED.Yesterday, DATEPICKER_PRESETS_MAPPED.Today, DATEPICKER_PRESETS_MAPPED.Current_week],
    maxRange: TicketsComponent.MAX_DATE_RANGE
  };
  public dateRangeConfig: DatePickerConfig = {
    autoBindDates: true,
    ...this._defaultDatePickerConfig
  };
  public dateRangeConfigMobile: DatePickerConfig = {
    autoBindDates: false,
    ...this._defaultDatePickerConfig
  };
  public eventStatusId: number = null;
  public eventStatuses: EventStatus[] = [];
  public salespoints: SalespointWithAlias[] = [];
  public salespointsFiltered: ReplaySubject<SalespointWithAlias[]> = new ReplaySubject<SalespointWithAlias[]>();

  public sortByOptions: SortByOption[] = [
    { id: 1, order: SortOrderEnum.Asc, title: 'general.api_ticket_tool_sort_by_date_asc' },
    { id: 2, order: SortOrderEnum.Desc, title: 'general.api_ticket_tool_sort_by_date_desc' }
  ];
  public sortOrder: SortOrderEnum = this.sortByOptions[0].order;
  public pageSize: number = 30;
  public page: number = 1;

  public results: PageableResult<SearchTicket>;
  public dataTableConfig: DataTable;
  public csvFilters: TicketsSearchFilters;
  public noSearches = true;
  private _paramsSubscription: Subscription;

  constructor(private ticketsService: TicketsService,
              private validationService: ValidationService,
              private routeService: RouteService,
              private viewService: ViewService,
              public eventService: EventService,
              public customerService: CustomerService,
              public salespointsService: SalespointsService,
              public utilService: UtilService,
              public authService: AuthService) {
  }

  public ngOnInit(): void {
    this.isLoading = true;
    const statusRequest: Observable<EventStatus[]> = this.eventService.findAllEventStatuses();
    const salespointRequest: Observable<SalespointWithAlias[]> = this.salespointsService.getSalesPoints();
    forkJoin([statusRequest, salespointRequest]).pipe(
      finalize(() => {
        this.isLoading = false;
        this._paramsSubscription = this.routeService.queryParams.subscribe(params => {
          if (this.noSearches) {
            Object.keys(params).forEach(paramKey => {
              if (Object.keys(this.filters).includes(paramKey)) {
                (this.filters as any)[paramKey] = params[paramKey];
              }
            });
            if (this.filters.eventId) {
              this.findEvent();
            }
            if (this.isFormValid) {
              this.search(Number(params.page) || 1);
            }
          }
        });
      })
    ).subscribe((results: [EventStatus[], SalespointWithAlias[]]) => {
      this.eventStatuses = (results[0] || []).filter((item: EventStatus) => !SELF_SERVICE_EXCLUDED_STATUSES.includes(item.id));
      this.salespoints = results[1] || [];
    });
  }
  public ngAfterViewInit(): void {
    this.setTableConfig();
  }
  public ngOnDestroy(): void {
    if (this._paramsSubscription) {
      this._paramsSubscription.unsubscribe();
    }
  }

  private findEvent(): void {
    this.eventService.search({eventIds: [this.filters.eventId]}).subscribe(result => {
      this.eventScrollLoad.object = result.items[0];
    });
  }

  public get isDetailView(): boolean {
    return this.routeService.checkRoute(['sales-terminal', 'tickets', 'view']);
  }

  public toggleFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }
  public clearFilters(): void {
    this.filters = {
      dateStart: null,
      dateEnd: null,
      ticketId: null,
      barcode: null,
      customerUuid: null,
      customerEmail: null,
      eventId: null,
      invoiceNumber: null,
      salespointId: null
    };
    this.customerScrollLoad.reset();
    this.eventScrollLoad.reset();
    this.eventStatusId = null;
  }

  public get isFormValid(): boolean {
    if (!this.validationService.emptyOrNumeric(this.filters.ticketId) || !this.validationService.emptyOrAlphaNumeric(this.filters.barcode) ||
      !this.isValidEmail(this.filters.customerEmail) || !this.validationService.emptyOrNumeric(this.filters.invoiceNumber) ||
      !this.checkEventAutocomplete || (this.filters.dateStart && !this.filters.dateEnd)) {
      return false;
    }
    if (!this.filters.ticketId && !this.filters.barcode && !this.filters.customerEmail && !this.filters.customerUuid && !this.filters.invoiceNumber && !this.filters.eventId) {
      if (!this.filters.dateStart || !this.filters.dateEnd) {
        return false;
      }
      const diff: number = moment(this.filters.dateEnd).diff(moment(this.filters.dateStart), 'days');
      return diff <= TicketsComponent.MAX_DATE_RANGE;
    }
    return true;
  }
  private isValidEmail(email: string): boolean {
    return !email?.length || ValidationService.validateEmail(email);
  }

  public search(page: number = 1): void {
    this.isLoading = true;
    const searchFilters: TicketsSearchFilters = cloneDeep(this.filters);
    this.page = page;
    searchFilters.page = page;
    searchFilters.pageSize = this.pageSize;
    searchFilters.sortOrder = this.sortOrder;
    if (searchFilters.dateStart) {
      searchFilters.dateStart = moment(searchFilters.dateStart).format('YYYY-MM-DD');
    }
    if (searchFilters.dateEnd) {
      searchFilters.dateEnd = moment(searchFilters.dateEnd).format('YYYY-MM-DD');
    }
    this.ticketsService.getTicketList(searchFilters).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((result: PageableResult<SearchTicket>) => {
      this.results = result;
      this.csvFilters = cloneDeep(searchFilters);
      this.noSearches = false;
      this.setFilterParams(searchFilters);
    });
  }
  public applyMobileDateChange(): void {
    if (this.isFormValid) {
      this.search();
    }
  }
  private setFilterParams(filters: TicketsSearchFilters): void {
    const filterParams: Params = {};
    Object.keys(filters).forEach(filterKey => {
      const filterValue: any = (filters as any)[filterKey];
      if (filterValue) {
        if (['dateStart', 'dateEnd'].includes(filterKey)) {
          filterParams[filterKey] = moment(filterValue).format(this.viewService.conf.filterDateFormat);
        } else {
          filterParams[filterKey] = filterValue;
        }
      }
    });
    this.routeService.replaceUrl('/app/sales-terminal/tickets',{queryParams: filterParams});
  }

  public downloadCsv(): void {
    this.isLoading = true;
    this.ticketsService.getTicketListCsv(this.csvFilters).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((res: string) =>
      this.utilService.downloadCSV(res, 'ticket_tool_list_' + moment(new Date()).format('YYYYMMDD')));
  }

  private get checkEventAutocomplete(): boolean {
    return !(this.eventScrollLoad.object && !this.filters.eventId);
  }

  public displayFilterSelectedOption(id: number, list: Array<any>): string {
    return find(list, {id: id})?.name;
  }

  public updateCustomer(customer: Customer): void {
    this.filters.customerUuid = customer?.uuid || null;
    this.filters.customerEmail = '';
  }

  public displaySortWith(order: SortOrderEnum): string | undefined {
    return find(this.sortByOptions, {order: order})?.title;
  }

  public openTicket(ticket: SearchTicket): void {
    this.routeService.navigate('app/sales-terminal/tickets/view/' + ticket.id);
  }
  public openEvent(eventId: number): void {
    this.routeService.navigate('app/back-office/events/view/' + eventId);
  }

  private setTableConfig(): void {
    this.dataTableConfig = {
      header: {
        items: [
          {
            key: 'ticket_id',
            format: 'general.api_ticket_tool_id',
            cssClass: 'break-word'
          },
          {
            key: 'barcode',
            format: 'general.api_ticket_tool_barcode',
            cssClass: 'break-word'
          },
          {
            key: 'status',
            format: 'general.api_ticket_tool_status',
            cssClass: 'break-word'
          },
          {
            key: 'operation_time',
            format: 'general.api_ticket_tool_operation_time',
            cssClass: 'break-word'
          },
          {
            key: 'event',
            format: 'general.api_ticket_tool_event',
            width: 20,
            cssClass: 'break-word'
          },
          {
            key: 'ticket_type',
            format: 'general.api_ticket_tool_ticket_type',
            cssClass: 'break-word'
          },
          {
            key: 'price',
            format: 'general.api_ticket_tool_price',
            cssClass: 'align-right break-word',
            showCurrency: true
          },
          {
            key: 'customer',
            format: 'general.api_ticket_tool_customer',
            cssClass: 'break-word',
            children: [
              {
                key: 'delivery_email',
                format: 'general.api_ticket_tool_delivery_email',
                cssClass: 'subtitle break-word'
              }
            ]
          },
          {
            key: 'salespoint',
            format: 'general.api_ticket_tool_salespoint',
            cssClass: 'break-word',
            children: [
              {
                key: 'seller',
                format: 'general.api_ticket_tool_seller',
                cssClass: 'subtitle break-word'
              }
            ]
          },
          {
            key: 'buttons'
          }
        ]
      },
      body: {
        items: [
          {
            key: 'ticket_id',
            format: 'id',
            useModel: true,
            type: DataTableRowItemType.Link,
            action: (item: any) => {
              this.openTicket(item);
            },
            mobileHeaderItem: true
          },
          {
            key: 'barcode',
            format: 'barcode',
            useModel: true
          },
          {
            key: 'status',
            type: DataTableRowItemType.Template,
            template: this.statusTemplate
          },
          {
            key: 'operation_time',
            type: DataTableRowItemType.Template,
            template: this.opTimeTemplate
          },
          {
            key: 'event',
            type: DataTableRowItemType.Template,
            template: this.eventTemplate
          },
          {
            key: 'ticket_type',
            type: DataTableRowItemType.Template,
            template: this.ticketTypeTemplate
          },
          {
            key: 'price',
            type: DataTableRowItemType.Template,
            template: this.priceTemplate,
            cssClass: 'white-space-no-wrap align-right'
          },
          {
            key: 'customer',
            type: DataTableRowItemType.Template,
            template: this.customerTemplate,
            children: [
              {
                key: 'delivery_email',
                type: DataTableRowItemType.Template,
                template: this.deliveryEmailTemplate
              }
            ]
          },
          {
            key: 'salespoint',
            type: DataTableRowItemType.Template,
            template: this.opSalespointTemplate,
            children: [
              {
                key: 'seller',
                type: DataTableRowItemType.Template,
                template: this.opSellerTemplate
              }
            ]
          },
          {
            key: 'buttons',
            type: DataTableRowItemType.Button,
            cssClass: 'align-right white-space-no-wrap',
            buttons: [
              {
                action: (item: any) => {
                  this.openTicket(item);
                },
                icon: 'arrow_right'
              }
            ],
            mobileHeaderItem: true
          }
        ]
      }
    };
  }

  protected readonly EventType = EventType;
}
